import React, { useCallback, useEffect, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loading } from './common/Loading';
import { Table } from './common/Table';
import axiosInstance from '../utils/axiosInstance';

const CategoryConfig = () => {
  const navigate = useNavigate();
  const { logout, getToken } = useAuth();
  const nameInputRef = useRef(null);

  const [categories, setCategories] = useState([]);
  const [description, setDescription] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const requireAuth = useCallback(() => {
    const token = getToken();
    if (!token) {
      logout();
      navigate('/login');
      return false;
    }
    return true;
  }, [getToken, logout, navigate]);

  const fetchCategories = useCallback(async () => {
    if (!requireAuth()) return;

    setError('');
    try {
      const res = await axiosInstance.get('/category');
      setCategories(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError(err.response?.data?.message || 'Failed to load categories. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [requireAuth]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleSave = async (editedRow) => {
    if (!requireAuth()) return false;

    setError('');
    setSuccess('');

    try {
      const payload = {
        name: String(editedRow.name || '').trim(),
        description: String(editedRow.description || '').trim(),
      };
      if (!payload.name) {
        setError('Category name cannot be empty.');
        return false;
      }

      await axiosInstance.put(`/category/${editedRow.uuid}`, payload);

      setCategories((prev) =>
        prev.map((category) =>
          category.uuid === editedRow.uuid ? { ...category, ...payload } : category
        )
      );
      setSuccess('Category updated.');
      return true;
    } catch (err) {
      console.error('Error updating category:', err);
      setError(err.response?.data?.message || 'Could not update category.');
      return false;
    }
  };

  const addCategory = async (event) => {
    event.preventDefault();
    if (!requireAuth()) return;

    const trimmedName = newCategory.trim();
    const trimmedDescription = description.trim();
    if (!trimmedName) {
      setError('Category name is required.');
      setSuccess('');
      return;
    }

    setAdding(true);
    setError('');
    setSuccess('');

    try {
      await axiosInstance.post('/category', {
        name: trimmedName,
        description: trimmedDescription,
      });
      setNewCategory('');
      setDescription('');
      setSuccess('Category created.');
      await fetchCategories();
      nameInputRef.current?.focus();
    } catch (err) {
      console.error('Error adding category:', err);
      setError(err.response?.data?.message || 'Could not create category.');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (row) => {
    if (!requireAuth()) return;
    if (!window.confirm(`Delete category "${row.name}"? This cannot be undone.`)) return;

    setError('');
    setSuccess('');
    try {
      await axiosInstance.delete(`/category/${row.uuid}`);
      setCategories((prev) => prev.filter((category) => category.uuid !== row.uuid));
      setSuccess('Category deleted.');
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(err.response?.data?.message || 'Could not delete category.');
    }
  };

  const columns = [
      { key: 'name', label: 'Name', sortable: true, editable: true, editType: 'text' },
      {
        key: 'description',
        label: 'Description',
        sortable: false,
        editable: true,
        editType: 'text',
        render: (value) => value || '—',
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (value, row, { isEditing, isSaving, handleEdit, handleSave: saveRow, handleCancel }) => {
          if (isEditing) {
            return (
              <Stack direction="row" spacing={1} onClick={(event) => event.stopPropagation()}>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<SaveOutlinedIcon fontSize="small" />}
                  onClick={(event) => {
                    event.stopPropagation();
                    saveRow();
                  }}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleCancel();
                  }}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              </Stack>
            );
          }

          return (
            <Stack direction="row" spacing={1} onClick={(event) => event.stopPropagation()}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditOutlinedIcon fontSize="small" />}
                onClick={(event) => {
                  event.stopPropagation();
                  handleEdit(row);
                }}
              >
                Edit
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon fontSize="small" />}
                onClick={(event) => {
                  event.stopPropagation();
                  handleDelete(row);
                }}
              >
                Delete
              </Button>
            </Stack>
          );
        },
      },
    ];

  if (loading) {
    return <Loading text="Loading categories..." fullScreen />;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, sm: 3 } }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={700} color="text.primary" gutterBottom>
            Categories
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and maintain inventory categories used across your catalog.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Add category
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Category name is required. Description is optional.
          </Typography>
          <Box component="form" onSubmit={addCategory}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                inputRef={nameInputRef}
                label="Category name"
                placeholder="e.g. Seating"
                value={newCategory}
                onChange={(event) => setNewCategory(event.target.value)}
                required
                fullWidth
                size="small"
              />
              <TextField
                label="Description"
                placeholder="Optional description for staff context"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                fullWidth
                size="small"
              />
              <Button
                type="submit"
                variant="contained"
                startIcon={<AddIcon />}
                disabled={adding}
                sx={{ alignSelf: { xs: 'stretch', md: 'center' }, whiteSpace: 'nowrap' }}
              >
                {adding ? 'Adding...' : 'Add category'}
              </Button>
            </Stack>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          {categories.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary" gutterBottom>
                No categories yet.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => nameInputRef.current?.focus()}
                sx={{ mt: 1 }}
              >
                Add your first category
              </Button>
            </Box>
          ) : (
            <Table
              columns={columns}
              data={categories}
              striped
              hoverable
              material
              searchable
              searchPlaceholder="Search categories..."
              paginated
              pageSize={10}
              onSave={handleSave}
              rowKey="uuid"
              emptyMessage="No categories found."
            />
          )}
        </Paper>
      </Stack>
    </Container>
  );
};

export default CategoryConfig;